<script lang="ts">
	import type { Content, LatLngExpression, Map, MapOptions, Marker } from 'leaflet';
	import 'leaflet/dist/leaflet.css';
	import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
	import 'leaflet.markercluster/dist/MarkerCluster.css';
	import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
	import { t } from 'svelte-i18n';
	import { onMount, SvelteComponent } from 'svelte';
	import type { MapMarker } from '../types/props';
	import { attrValue, shortId } from '../utils/html';

	/**
	 * Example: https://svelte.dev/repl/62271e8fda854e828f26d75625286bc3?version=3.38.3
	 */

	export let initialPointOfView: LatLngExpression = [46.232193, 2.209667];
	export let options: Partial<MapOptions> = {};
	export let zoom = 4.5;
	export let noZoomControl = false;
	export let markers: MapMarker[] = [];
	export let classNames: string = null;

	let map: Map;
	let id = `map_${shortId()}`;

	const resizeMap = () => {
		map && map.invalidateSize();
	};

	const bindPopup = (m: Marker, fn: (e: HTMLElement) => Content) => {
		let popupComponent;
		m.bindPopup(() => {
			let container = L.DomUtil.create('div');
			popupComponent = fn(container);
			return container;
		});

		m.on('popupclose', () => {
			if (popupComponent) {
				let old: SvelteComponent = popupComponent;
				popupComponent = null;
				setTimeout(() => {
					old.$destroy();
				}, 500);
			}
		});

		return m;
	};

	onMount(async () => {
		try {
			const L = await import('leaflet');

			const createMarker = (m: MapMarker) => {
				let item = L.marker([m.lat, m.long], {
					icon: L.icon({
						iconUrl: 'https://wiseed-medias.s3.amazonaws.com/webpages/map-marker.svg',
						iconSize: [48, 48],
						iconAnchor: [25, 40],
						popupAnchor: [0, -30]
					})
				});

				if (m.popupContent) {
					if (typeof m.popupContent == 'string') {
						item.bindPopup('<span>' + m.popupContent + '</span>');
					} else {
						let component = m.popupContent;
						item = bindPopup(item, (container) => {
							return new component.component({
								target: container,
								props: { ...component.props }
							});
						});
					}
				}

				return item;
			};

			let { default: GestureHandling } = await import('leaflet-gesture-handling');
			if (GestureHandling) {
				L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
			}

			const opts: MapOptions & {
				gestureHandling: boolean;
				gestureHandlingText: Record<string, string>;
			} = {
				preferCanvas: true,
				center: initialPointOfView,
				zoom: zoom,
				zoomControl: !noZoomControl,
				gestureHandling: true,
				gestureHandlingText: {
					touch: $t('maps.control.touch') as string,
					scroll: $t('maps.control.scroll') as string,
					scrollMac: $t('maps.control.scrollMac') as string
				},
				...options
			};

			map = L.map(id, opts);

			let tileLink =
				'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
			let tileOpt = {
				attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
				minZoom: 1,
				maxZoom: 16
			};

			L.tileLayer(tileLink, tileOpt).addTo(map);

			if (markers?.length) {
				let markersLayer = L.featureGroup();

				markers.forEach((e) => {
					let markerCreated = createMarker(e);
					markerCreated.addTo(markersLayer);
				});

				map = map.addLayer(markersLayer);
			}
		} catch (e) {
			console.error(e);
		}
	});
</script>

<svelte:window on:resize={resizeMap} />

<div {id} class={attrValue(['map relative', classNames])}>
	<slot />
</div>

<style lang="postcss">
	.map {
		@apply aspect-square md:aspect-video w-full h-full z-1;
	}

	:global(.cluster-marker) {
		height: 40px;
		width: 40px;
		border-radius: 50%;
		background-color: theme('colors.grey-dark');
		color: theme('colors.white');
		text-align: center;
		font-size: 20px;
		line-height: 40px;
		margin-top: -20px;
		margin-left: -20px;
	}
</style>
